import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react'
import { canWorkQueue, hasPerm, useMe } from '@/shared/api/session'
import { decideWriteAccess, type WriteAccessDecision } from './rule'

export { READ_ONLY_TITLE, writeControlProps, type WriteControlProps } from './rule'
import { readStored, writeStored } from '@/shared/lib/storage'

/**
 * May this viewer change data, or are they only looking?
 *
 * The rule itself is in ./rule.ts as a pure function, so it can be checked
 * without a renderer -- see rule.test.ts. This file is only the plumbing:
 * where the flag is stored and who is allowed to flip it.
 *
 * canEdit is stated positively on purpose. As readOnly the fail-safe ran the
 * wrong way -- anything that failed to produce a value read as false, i.e.
 * "writes allowed". Here an absent or unknown value means no edits.
 */
interface WriteAccess extends WriteAccessDecision {
  setEditing: (on: boolean) => void
}

const KEY = 'editMode'

// sessionStorage rather than localStorage so it re-arms next session instead
// of silently staying open forever.
function loadArmed(): boolean {
  try {
    return readStored(KEY, { store: 'session' }) === '1'
  } catch {
    return false
  }
}

const WriteAccessContext = createContext<WriteAccess | null>(null)

export function WriteAccessProvider({
  queueOwner = null,
  children,
}: {
  /** Whose Focus queue is on screen; null means the viewer's own. */
  queueOwner?: string | null
  children: ReactNode
}) {
  const { data: me } = useMe()
  const [armed, setArmed] = useState(loadArmed)
  const [armedFor, setArmedFor] = useState<string | null>(queueOwner)

  const setEditing = useCallback((on: boolean) => {
    setArmed(on)
    setArmedFor(queueOwner)
    try {
      writeStored(KEY, on ? '1' : '0', { store: 'session' })
    } catch {
      /* private window or quota — the default (read-only) is the safe one */
    }
  }, [queueOwner])

  const value = useMemo<WriteAccess>(() => ({
    ...decideWriteAccess({
      isSeller: me ? canWorkQueue(me) : null,
      managesLeads: hasPerm(me, 'manage_leads'),
      queueOwner,
      armed,
      armedFor,
    }),
    setEditing,
  }), [me, queueOwner, armed, armedFor, setEditing])

  return <WriteAccessContext.Provider value={value}>{children}</WriteAccessContext.Provider>
}

export function useWriteAccess(): WriteAccess {
  const v = useContext(WriteAccessContext)
  if (!v) throw new Error('useWriteAccess must be used inside a WriteAccessProvider')
  return v
}

/** The common case: one boolean, for the mutations and the controls they back. */
export function useCanEdit(): boolean {
  return useWriteAccess().canEdit
}
